/**
 * ScheduleComplianceReport — Compliance score per punëtor
 * Sa herë ishte në kohë, vonë, doli herët, ka bërë orë shtesë
 * Bazuar te /api/reports/schedule-compliance (ekzistues) + /api/reports/attendance-summary
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Avatar, Button, TextField, MenuItem, Stack, CircularProgress, LinearProgress, Tooltip, Grid } from '@mui/material';
import SearchIcon        from '@mui/icons-material/Search';
import WarningAmberIcon  from '@mui/icons-material/WarningAmber';
import MoreTimeIcon      from '@mui/icons-material/MoreTime';
import { useTranslation } from 'react-i18next';
import * as XLSX         from 'xlsx';
import jsPDF             from 'jspdf';
import autoTable         from 'jspdf-autotable';
import apiService        from '../../services/apiServices';
import { computeAttendanceSummary } from '../../services/attendanceDemoService';

import { getAuthHeaders } from '../../services/authSession';
import { API_URL, isDemoMode } from '../../services/backendMode';
import { ReportPage, ExportMenu } from '../report/ReportKit';

async function fetchCompliance(params) {
  if (isDemoMode()) {
    const data = computeAttendanceSummary(params);
    return { success: true, data };
  }
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v))
  ).toString();
  const res = await fetch(`${API_URL}/api/reports/attendance-summary?${qs}`, {
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
  });
  return res.json().catch(() => ({ success: false, data: [] }));
}

// Dita LOKALE: `toISOString()` është UTC dhe para orës 02:00 jepte ditën e djeshme.
const localYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const monthStart = () => { const d = new Date(); d.setDate(1); return localYMD(d); };
const todayStr   = () => localYMD(new Date());

function fmtMin(min) {
  const m = Math.max(0, Math.round(Number(min) || 0));
  if (m === 0) return '0m';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
}

function initials(name) {
  return (name || '').split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

// Compliance score: % e ditëve "on_time" nga ditët totale
function complianceScore(worker) {
  const active = worker.days - worker.absent;
  if (active === 0) return 0;
  return Math.round((worker.on_time / active) * 100);
}

function ScoreBar({ score }) {
  const color = score >= 90 ? 'success' : score >= 70 ? 'warning' : 'error';
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={0.3}>
        <Typography variant="caption" fontWeight={600} color={`${color}.main`}>{score}%</Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={score}
        color={color}
        sx={{ borderRadius: 2, height: 6 }}
      />
    </Box>
  );
}

function aggregate(rows) {
  const map = {};
  rows.forEach(r => {
    const k = String(r.worker_id);
    if (!map[k]) map[k] = {
      worker_id:       r.worker_id,
      worker_name:     r.worker_name,
      department_name: r.department_name,
      schedule_name:   r.schedule_name,
      days:            0,
      absent:          0,
      late:            0,
      early_checkout:  0,
      overtime_days:   0,
      on_time:         0,
      total_late_min:      0,
      total_early_min:     0,
      total_overtime_min:  0,
      total_worked_min:    0,
    };
    const w = map[k];
    w.days++;
    const st = r.compliance_status;
    if (st === 'absent')         w.absent++;
    else if (st === 'late')      { w.late++;         w.total_late_min     += Number(r.late_minutes || 0); }
    else if (st === 'early_checkout') { w.early_checkout++; w.total_early_min  += Number(r.early_checkout_minutes || 0); }
    else if (st === 'on_time' || st === 'in_progress') w.on_time++;
    if (Number(r.overtime_minutes) > 0) { w.overtime_days++; w.total_overtime_min += Number(r.overtime_minutes || 0); }
    w.total_worked_min += Number(r.worked_minutes || 0);
  });
  return Object.values(map).sort((a, b) => complianceScore(b) - complianceScore(a));
}

export default function ScheduleComplianceReport() {
  const { t } = useTranslation();
  const [dateFrom,    setDateFrom]    = useState(monthStart());
  const [dateTo,      setDateTo]      = useState(todayStr());
  const [fDept,       setFDept]       = useState('');
  const [departments, setDepts]       = useState([]);
  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(false);

  useEffect(() => {
    apiService().get('departments').then(r => { if (r?.data) setDepts(r.data); }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchCompliance({
        from:          dateFrom,
        to:            dateTo,
        department_id: fDept || undefined,
      });
      setRows(res?.data || []);
    } catch (e) {
      console.error('[ScheduleComplianceReport]', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, fDept]);

  const workers = useMemo(() => aggregate(rows), [rows]);

  // Overall company compliance
  const companyScore = useMemo(() => {
    if (workers.length === 0) return null;
    return Math.round(workers.reduce((s, w) => s + complianceScore(w), 0) / workers.length);
  }, [workers]);

  function exportXlsx() {
    const data = workers.map(w => ({
      'Punëtori':         w.worker_name,
      'Departamenti':     w.department_name,
      'Orari':            w.schedule_name,
      'Ditë totale':      w.days,
      'Mungues':          w.absent,
      'Vonesa':           w.late,
      'Doli herët':       w.early_checkout,
      'Orë shtesë (ditë)':w.overtime_days,
      'Min vonesa':       w.total_late_min,
      'Min herët':        w.total_early_min,
      'Min shtesë':       w.total_overtime_min,
      'Orë totale punë':  fmtMin(w.total_worked_min),
      'Score %':          complianceScore(w),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Compliance');
    XLSX.writeFile(wb, `schedule-compliance-${dateFrom}-${dateTo}.xlsx`);
  }

  function exportPdf() {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.text(`Schedule Compliance — ${dateFrom} deri ${dateTo}`, 14, 15);
    autoTable(doc, {
      startY: 22,
      head: [['Punëtori', 'Dept', 'Orari', 'Ditë', 'Mungues', 'Vonesa', 'Herët', 'Shtesë', 'Min vonë', 'Min herët', 'Min shtesë', 'Orë punë', 'Score']],
      body: workers.map(w => [
        w.worker_name, w.department_name || '', w.schedule_name || '',
        w.days, w.absent, w.late, w.early_checkout, w.overtime_days,
        fmtMin(w.total_late_min), fmtMin(w.total_early_min), fmtMin(w.total_overtime_min),
        fmtMin(w.total_worked_min), `${complianceScore(w)}%`,
      ]),
      styles: { fontSize: 7 },
    });
    doc.save(`schedule-compliance-${dateFrom}-${dateTo}.pdf`);
  }

  return (
    <ReportPage>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{t('report.complianceTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('report.complianceSubtitle')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <ExportMenu onExcel={exportXlsx} onPdf={exportPdf} />
        </Stack>
      </Stack>

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" gap={1} alignItems="flex-end">
          <TextField type="date" size="small" label={t('report.from')} value={dateFrom}
            onChange={e => setDateFrom(e.target.value)} sx={{ width: 160 }} InputLabelProps={{ shrink: true }} />
          <TextField type="date" size="small" label={t('report.to')} value={dateTo}
            onChange={e => setDateTo(e.target.value)} sx={{ width: 160 }} InputLabelProps={{ shrink: true }} />
          <TextField select size="small" label={t('report.department')} value={fDept}
            onChange={e => setFDept(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">{t('report.all')}</MenuItem>
            {departments.map(d => (
              <MenuItem key={d.Id || d.id} value={String(d.Id || d.id)}>
                {d.Name || d.name}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" startIcon={<SearchIcon />} onClick={load} disabled={loading}
            sx={{ borderRadius: 2 }}>
            {loading ? <CircularProgress size={18} color="inherit" /> : t('report.search')}
          </Button>
        </Stack>
      </Paper>

      {/* Company score */}
      {companyScore !== null && !loading && (
        <Paper elevation={2} sx={{ p: 2.5, mb: 3, borderRadius: 3 }}>
          <Stack direction="row" spacing={3} alignItems="center">
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                SCORE I PËRGJITHSHËM I KOMPANISË
              </Typography>
              <Typography variant="h3" fontWeight={800}
                color={companyScore >= 90 ? 'success.main' : companyScore >= 70 ? 'warning.main' : 'error.main'}>
                {companyScore}%
              </Typography>
            </Box>
            <Box flex={1}>
              <LinearProgress
                variant="determinate"
                value={companyScore}
                color={companyScore >= 90 ? 'success' : companyScore >= 70 ? 'warning' : 'error'}
                sx={{ height: 12, borderRadius: 3 }}
              />
            </Box>
            <Grid container spacing={2} sx={{ maxWidth: 400 }}>
              {[
                { label: 'Punëtorë', value: workers.length,                              color: 'text.primary' },
                { label: 'Vonesa >', value: workers.filter(w => w.late > 0).length,      color: 'warning.main' },
                { label: 'Herët >',  value: workers.filter(w => w.early_checkout > 0).length, color: 'warning.main' },
                { label: 'Shtesë >', value: workers.filter(w => w.overtime_days > 0).length,  color: 'info.main' },
              ].map(s => (
                <Grid item xs={6} key={s.label}>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                  <Typography variant="body1" fontWeight={700} color={s.color}>{s.value}</Typography>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Paper>
      )}

      {/* Table */}
      <Paper elevation={2} sx={{ borderRadius: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>{t('report.worker')}</TableCell>
                  <TableCell>{t('report.department')}</TableCell>
                  <TableCell>{t('report.scheduleCol')}</TableCell>
                  <TableCell align="right">{t('report.days')}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('report.absenceTooltip')}><span>{t('report.absentLabel')}</span></Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('report.latenessTooltip')}><span><WarningAmberIcon sx={{ fontSize: 14 }} /> {t('report.latenessCard')}</span></Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('report.earlyTooltip')}><span>{t('report.earlyCheckout')}</span></Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('report.extraTooltip')}><span><MoreTimeIcon sx={{ fontSize: 14 }} /> {t('report.extraCol')}</span></Tooltip>
                  </TableCell>
                  <TableCell align="right">{t('report.lateMin')}</TableCell>
                  <TableCell align="right">{t('report.extraMin')}</TableCell>
                  <TableCell align="right">{t('report.workedHoursCol')}</TableCell>
                  <TableCell>Compliance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workers.map((w, i) => {
                  const score = complianceScore(w);
                  return (
                    <TableRow key={w.worker_id || i} hover>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{i + 1}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar sx={{ width: 28, height: 28, fontSize: 11,
                            bgcolor: score >= 90 ? 'success.light' : score >= 70 ? 'warning.light' : 'error.light' }}>
                            {initials(w.worker_name)}
                          </Avatar>
                          <Typography variant="body2" fontWeight={500}>{w.worker_name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell><Typography variant="body2" color="text.secondary">{w.department_name || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="body2" color="text.secondary">{w.schedule_name || '—'}</Typography></TableCell>
                      <TableCell align="right">{w.days}</TableCell>
                      <TableCell align="right">
                        {w.absent > 0
                          ? <Typography color="error.main" fontWeight={600}>{w.absent}</Typography>
                          : <Typography color="text.disabled">0</Typography>}
                      </TableCell>
                      <TableCell align="right">
                        {w.late > 0
                          ? <Typography color="warning.main" fontWeight={600}>{w.late}</Typography>
                          : <Typography color="text.disabled">0</Typography>}
                      </TableCell>
                      <TableCell align="right">
                        {w.early_checkout > 0
                          ? <Typography color="warning.main" fontWeight={600}>{w.early_checkout}</Typography>
                          : <Typography color="text.disabled">0</Typography>}
                      </TableCell>
                      <TableCell align="right">
                        {w.overtime_days > 0
                          ? <Typography color="info.main" fontWeight={600}>{w.overtime_days}</Typography>
                          : <Typography color="text.disabled">0</Typography>}
                      </TableCell>
                      <TableCell align="right">
                        <Typography color={w.total_late_min > 0 ? 'warning.main' : 'text.disabled'} variant="body2">
                          {fmtMin(w.total_late_min)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography color={w.total_overtime_min > 0 ? 'info.main' : 'text.disabled'} variant="body2">
                          {fmtMin(w.total_overtime_min)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>{fmtMin(w.total_worked_min)}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 130 }}>
                        <ScoreBar score={score} />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {workers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">
                        {t('report.noDataSelectPeriod')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </ReportPage>
  );
}
